/**
 * ResultsPage (Unified)
 *
 * Displays results for both single and multipart search using the multipart API.
 * Features:
 * - Progress bar showing search status
 * - Sequential source priority: TVS -> Boodmo -> Smart
 * - Source sections as main headings
 * - Part names as subheadings under each source
 * - Horizontal carousel for each part's results
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useMultipartSearchParallel,
  VehicleContext,
  SourceStatus,
} from '../hooks/useMultipartSearch';
import { useToast } from '../components/Toast';
import '../App.css';

// Icons
const CarouselArrowLeft = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18L9 12L15 6" stroke="#555555" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const CarouselArrowRight = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 18L15 12L9 6" stroke="#555555" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const WarningIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

// Main component
function ResultsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();

  // Get search params from URL - support both single and multipart formats
  const urlParams = new URLSearchParams(location.search);
  const query = urlParams.get('q') || '';
  const partsParam = urlParams.get('parts') || '';
  const subAggregate = urlParams.get('subAggregate') || '';
  const make = urlParams.get('make') || '';
  const model = urlParams.get('model') || '';
  const variant = urlParams.get('variant') || '';
  const fuelType = urlParams.get('fuelType') || '';
  const year = urlParams.get('year');
  const correlationId = urlParams.get('correlationId') || undefined;  // Link log entries from validation

  const [copiedKeys, setCopiedKeys] = useState<Set<string>>(new Set());

  // Carousel refs for each source-part combination
  const carouselRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [carouselScroll, setCarouselScroll] = useState<Map<string, { canScrollLeft: boolean; canScrollRight: boolean }>>(new Map());

  const { isLoading, error, data, sourceStatuses, searchByQuery, searchByParts } = useMultipartSearchParallel();

  // Highlight search terms in text
  const highlightSearchTerms = (text: string, searchQuery: string): React.ReactNode => {
    if (!searchQuery || !text) return text;
    try {
      const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
      return parts.map((part, i) =>
        part.toLowerCase() === searchQuery.toLowerCase() ? (
          <mark key={i} className="bg-yellow-200 text-gray-900">{part}</mark>
        ) : part
      );
    } catch {
      return text;
    }
  };

  // Handle copy
  const handleCopy = async (key: string, partNumber: string) => {
    try {
      await navigator.clipboard.writeText(partNumber);
      setCopiedKeys(prev => new Set(Array.from(prev).concat(key)));
      addToast({ type: 'success', title: 'Copied', message: 'Part number copied to clipboard' });
      setTimeout(() => {
        setCopiedKeys(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }, 2000);
    } catch {
      addToast({ type: 'error', title: 'Error', message: 'Failed to copy part number' });
    }
  };

  // Check scroll position for a carousel
  const checkScrollPosition = (carouselKey: string) => {
    const carousel = carouselRefs.current.get(carouselKey);
    if (!carousel) return;
    const { scrollLeft, scrollWidth, clientWidth } = carousel;
    const canScrollLeft = scrollLeft > 0;
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;

    setCarouselScroll(prev => {
      const next = new Map(prev);
      next.set(carouselKey, { canScrollLeft, canScrollRight });
      return next;
    });
  };

  // Carousel scroll function
  const scrollCarousel = (carouselKey: string, direction: 'left' | 'right') => {
    const carousel = carouselRefs.current.get(carouselKey);
    if (!carousel) return;
    const scrollAmount = carousel.offsetWidth * 0.75;
    const scrollDirection = direction === 'left' ? -scrollAmount : scrollAmount;
    carousel.scrollBy({
      left: scrollDirection,
      behavior: 'smooth'
    });
    setTimeout(() => {
      checkScrollPosition(carouselKey);
    }, 300);
  };

  // Set carousel ref
  const setCarouselRef = (key: string, el: HTMLDivElement | null) => {
    if (el) {
      carouselRefs.current.set(key, el);
      setTimeout(() => checkScrollPosition(key), 100);
    }
  };

  // Execute search on mount using SSE streaming
  useEffect(() => {
    const vehicle: VehicleContext = {
      make: make || undefined,
      model: model || undefined,
      variant: variant || undefined,
      fuelType: fuelType || undefined,
      year: year ? parseInt(year) : undefined,
    };

    // Handle different search input formats
    // Pass correlationId to link log entries from validation phase
    if (query) {
      // Single part search with query text
      searchByQuery(query, vehicle, correlationId);
    } else if (partsParam) {
      // Multipart search with explicit parts list
      const parts = partsParam.split(',').map((p: string) => ({
        subAggregate: p.trim(),
      }));
      searchByParts(parts, vehicle, correlationId);
    } else if (subAggregate) {
      // Single part with explicit subAggregate
      searchByParts([{ subAggregate }], vehicle, correlationId);
    }
  }, [query, partsParam, subAggregate, make, model, variant, fuelType, year, correlationId]);

  // Build display query string for header - use part names from API response when available
  const getDisplayTitle = () => {
    // If we have API data, use the part names from partsSummary
    if (data?.partsSummary && data.partsSummary.length > 0) {
      const partNames = data.partsSummary
        .map((p: { partKey?: string; originalInput?: string }) => p.partKey || p.originalInput)
        .filter(Boolean);
      if (partNames.length > 0) {
        return partNames.join(', ').toUpperCase();
      }
    }
    // Fallback to URL params
    if (partsParam) {
      return partsParam.split(',').map((p: string) => p.trim()).join(', ').toUpperCase();
    }
    if (subAggregate) {
      return subAggregate.toUpperCase();
    }
    // For query, try to extract just the part name (not vehicle info)
    // This is a fallback - ideally the API provides normalized part names
    return query || '';
  };

  const displayTitle = getDisplayTitle();

  // Build vehicle string for header - use data if available, otherwise use URL params
  const vehicleString = data?.vehicle
    ? [data.vehicle.make, data.vehicle.model, data.vehicle.variant, data.vehicle.fuelType, data.vehicle.year]
        .filter(Boolean)
        .join(' ')
    : [make, model, variant, fuelType, year].filter(Boolean).join(' ');

  const handleBackToSearch = () => {
    navigate('/');
  };

  // Get best source for a specific part (TVS > Boodmo > Smart)
  const getBestSourceForPart = (partKey: string): string | null => {
    if (!data?.results) return null;

    // Check TVS first
    const tvsResults = data.results['tvs']?.[partKey] || [];
    if (tvsResults.length > 0) return 'tvs';

    // Then Boodmo
    const boodmoResults = data.results['boodmo']?.[partKey] || [];
    if (boodmoResults.length > 0) return 'boodmo';

    // Then Smart
    const smartResults = data.results['smart']?.[partKey] || [];
    if (smartResults.length > 0) return 'smart';

    return null;
  };

  // Build per-part source mapping: { partKey: { source, results } }
  const getPartSourceMapping = (): Map<string, { source: string; results: any[] }> => {
    const mapping = new Map<string, { source: string; results: any[] }>();
    if (!data?.results) return mapping;

    // Collect all unique part keys from all sources
    const allPartKeys = new Set<string>();
    ['tvs', 'boodmo', 'smart'].forEach(source => {
      const sourceResults = data.results[source] || {};
      Object.keys(sourceResults).forEach(key => allPartKeys.add(key));
    });

    // For each part, find the best source
    allPartKeys.forEach(partKey => {
      const bestSource = getBestSourceForPart(partKey);
      if (bestSource) {
        const results = data.results[bestSource]?.[partKey] || [];
        if (results.length > 0) {
          mapping.set(partKey, { source: bestSource, results });
        }
      }
    });

    return mapping;
  };

  const partSourceMapping = data ? getPartSourceMapping() : new Map();

  // Group parts by source for display
  const getPartsGroupedBySource = (): Map<string, Map<string, any[]>> => {
    const grouped = new Map<string, Map<string, any[]>>();

    partSourceMapping.forEach(({ source, results }, partKey) => {
      if (!grouped.has(source)) {
        grouped.set(source, new Map());
      }
      grouped.get(source)!.set(partKey, results);
    });

    return grouped;
  };

  const partsGroupedBySource = data ? getPartsGroupedBySource() : new Map();

  // Count total results across all displayed parts
  const getTotalDisplayedResults = (): number => {
    let total = 0;
    partSourceMapping.forEach(({ results }) => {
      total += results.length;
    });
    return total;
  };

  // Get sources that have parts to display (in priority order)
  const getDisplaySources = (): string[] => {
    const sources: string[] = [];
    ['tvs', 'boodmo', 'smart'].forEach(source => {
      if (partsGroupedBySource.has(source)) {
        sources.push(source);
      }
    });
    return sources;
  };

  const displaySources = data ? getDisplaySources() : [];
  const hasResults = displaySources.length > 0;

  // Render result card - premium design
  const renderResultCard = (result: any, source: string) => {
    const partNumber = result.partNumber || result.part_number || '';
    const resultKey = `${partNumber}-${result.id || Math.random()}`;

    const colorCls = source === 'tvs'
      ? 'border-[#FF6C16]/30 bg-[#FF6C16]/10 text-[#FF6C16]'
      : source === 'boodmo'
        ? 'border-[#12477A]/30 bg-[#12477A]/10 text-[#12477A]'
        : 'border-[#A64EC6]/30 bg-[#A64EC6]/10 text-[#A64EC6]';

    const titleColorClass = source === 'tvs'
      ? 'result-title-tvs'
      : source === 'boodmo'
        ? 'result-title-boodmo'
        : 'result-title-smart';

    const labelColorClass = source === 'tvs'
      ? 'spec-label-tvs'
      : source === 'boodmo'
        ? 'spec-label-boodmo'
        : 'spec-label-smart';

    const cardAccentClass = source === 'tvs'
      ? 'card-accent-tvs'
      : source === 'boodmo'
        ? 'card-accent-boodmo'
        : 'card-accent-smart';

    return (
      <div
        key={resultKey}
        className={`flex-shrink-0 apple-card-hover ${cardAccentClass}`}
        style={{
          backgroundColor: '#fff',
          borderRadius: '18px',
          boxShadow: '2px 4px 12px #00000014',
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'stretch',
          minHeight: '29.4117647059rem',
          position: 'relative',
          transition: 'all 0.3s cubic-bezier(0, 0, 0.5, 1)',
          whiteSpace: 'normal',
          width: '28.2352941176rem',
          maxWidth: '28.2352941176rem',
          minWidth: '280px',
          padding: '1.5rem'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', flex: '1', minHeight: '0' }}>
          <div className="result-header-premium">
            <div className="flex-1" style={{ minHeight: '0' }}>
              <h3 className={`result-title-premium ${titleColorClass}`}>
                {highlightSearchTerms(String(result.name || result.title || result.partDescription || ''), query)}
              </h3>
              {result.description &&
               result.description !== result.name &&
               result.description !== result.title &&
               result.description !== result.partDescription && (
                <p className="result-description-premium">
                  {highlightSearchTerms(String(result.description), query)}
                </p>
              )}
            </div>
            <div className={`badge-premium ${
              source === 'tvs' ? 'badge-tvs' :
              source === 'boodmo' ? 'badge-boodmo' :
              'badge-smart'
            }`}>
              <span className="ml-1">
                {source === 'tvs' ? 'TVS' :
                 source === 'boodmo' ? 'Boodmo' :
                 'Smart'}
              </span>
            </div>
          </div>
          <div className="result-specs-premium">
            {/* Part Number */}
            <div className="spec-item-premium">
              <span className={`spec-label-premium ${labelColorClass}`}>Part Number</span>
              <span className="spec-value-premium flex items-center gap-2 flex-wrap">
                {partNumber ? (
                  <>
                    <button
                      type="button"
                      className={`px-2 py-0.5 text-xs rounded-full border ${colorCls} whitespace-nowrap overflow-hidden text-ellipsis max-w-[70%]`}
                      title="Copy part number"
                      onClick={() => handleCopy(resultKey, partNumber)}
                    >
                      {partNumber}
                    </button>
                    <button
                      type="button"
                      className={`text-xs px-2 py-0.5 border rounded shrink-0 ${copiedKeys.has(resultKey) ? 'bg-green-100 border-green-300 text-green-800' : 'hover:bg-gray-50'}`}
                      onClick={() => handleCopy(resultKey, partNumber)}
                    >
                      {copiedKeys.has(resultKey) ? 'Copied' : 'Copy'}
                    </button>
                  </>
                ) : (
                  <span>-</span>
                )}
              </span>
            </div>

            {/* Brand */}
            <div className="spec-item-premium">
              <span className={`spec-label-premium ${labelColorClass}`}>Brand</span>
              <span className="spec-value-premium">
                {(result.brandName || result.brand) ? (
                  <span className="inline-flex items-center gap-1">
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${colorCls}`}>
                      {result.brandName || result.brand}
                    </span>
                    {result.brandPriority && (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full"
                        title="Top Selling Brand"
                      >
                        &#9733;
                      </span>
                    )}
                  </span>
                ) : (
                  <span>-</span>
                )}
              </span>
            </div>

            {/* MRP */}
            {result.mrp && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>
                  {result.priceType || 'MRP'}
                </span>
                <span className="spec-value-premium font-semibold text-green-700">
                  ₹{typeof result.mrp === 'string'
                    ? (result.mrp.includes('.') ? parseFloat(result.mrp).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : result.mrp)
                    : result.mrp.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* List Price */}
            {result.listPrice && result.listPrice !== result.mrp && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>List Price</span>
                <span className="spec-value-premium">
                  ₹{typeof result.listPrice === 'string'
                    ? (result.listPrice.includes('.') ? parseFloat(result.listPrice).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : result.listPrice)
                    : result.listPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* Special Net */}
            {result.splNet && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>Special Net</span>
                <span className="spec-value-premium font-semibold text-blue-700">
                  ₹{typeof result.splNet === 'string'
                    ? (result.splNet.includes('.') ? parseFloat(result.splNet).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : result.splNet)
                    : result.splNet.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* Vehicle */}
            <div className="spec-item-premium">
              <span className={`spec-label-premium ${labelColorClass}`}>Vehicle</span>
              <span className="spec-value-premium">{result.vehicleMake} {result.vehicleModel}</span>
            </div>

            {/* Sub Model */}
            {result.vehicleSubModel && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>Sub Model</span>
                <span className="spec-value-premium">{result.vehicleSubModel}</span>
              </div>
            )}

            {/* Fuel Type */}
            {result.vehicleFuelType && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>Fuel Type</span>
                <span className="spec-value-premium">{result.vehicleFuelType}</span>
              </div>
            )}

            {/* Year Range */}
            {(result.vehicleFromYear || result.vehicleToYear) && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>Year Range</span>
                <span className="spec-value-premium">
                  {result.vehicleFromYear && result.vehicleToYear
                    ? `${result.vehicleFromYear} - ${result.vehicleToYear}`
                    : result.vehicleFromYear || result.vehicleToYear}
                </span>
              </div>
            )}

            {/* Category */}
            <div className="spec-item-premium">
              <span className={`spec-label-premium ${labelColorClass}`}>Category</span>
              <span className="spec-value-premium">{result.aggregate || result.category || 'General'}</span>
            </div>

            {/* Sub Category */}
            {result.subAggregate && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>Sub Category</span>
                <span className="spec-value-premium">{result.subAggregate}</span>
              </div>
            )}

            {/* Part Description */}
            {result.partDescription &&
             result.partDescription !== result.name &&
             result.partDescription !== result.title && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>Description</span>
                <span className="spec-value-premium">{result.partDescription}</span>
              </div>
            )}

            {/* OEM Part Information */}
            {(result.oemPartNumber || result.oemPartBrand) && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>OEM Part</span>
                <span className="spec-value-premium">
                  {result.oemPartNumber && (
                    <span className="mr-2">{result.oemPartNumber}</span>
                  )}
                  {result.oemPartBrand && (
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${colorCls} opacity-75`}>
                      {result.oemPartBrand}
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* Alternative Part Information */}
            {(result.alternativePartNo || result.alternativePartBrand) && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>Alternative Part</span>
                <span className="spec-value-premium">
                  {result.alternativePartNo && (
                    <span className="mr-2">{result.alternativePartNo}</span>
                  )}
                  {result.alternativePartBrand && (
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${colorCls} opacity-75`}>
                      {result.alternativePartBrand}
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* TVS Part Number (for Boodmo results) */}
            {result.tvsPartNumber && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>TVS Part Number</span>
                <span className="spec-value-premium">{result.tvsPartNumber}</span>
              </div>
            )}

            {/* OES Equivalent */}
            {result.oenEquivalent && result.oenEquivalent.length > 0 && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>OES Equivalent</span>
                <span className="spec-value-premium">{result.oenEquivalent.join(', ')}</span>
              </div>
            )}

            {/* Components */}
            {result.components && result.components.length > 0 && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>Components</span>
                <span className="spec-value-premium">{result.components.join(', ')}</span>
              </div>
            )}

            {/* Engine Code */}
            {result.engineCode && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>Engine Code</span>
                <span className="spec-value-premium">{result.engineCode}</span>
              </div>
            )}

            {/* Type */}
            {result.type && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>Type</span>
                <span className="spec-value-premium">{result.type}</span>
              </div>
            )}

            {/* Notes */}
            {result.notes && (
              <div className="spec-item-premium">
                <span className={`spec-label-premium ${labelColorClass}`}>Notes</span>
                <span className="spec-value-premium">{result.notes}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Get source display info
  const getSourceInfo = (source: string) => {
    switch (source) {
      case 'tvs':
        return {
          title: 'TVS Catalog.',
          subtitle: 'Exclusive Auto Parts Catalog Database.',
          logo: '/images/tvs-logo1.png',
          color: '#FF6C16',
          badgeClass: 'bg-[#FF6C16]'
        };
      case 'boodmo':
        return {
          title: 'Boodmo Catalog.',
          subtitle: 'Comprehensive Parts Marketplace.',
          logo: '/images/boodmo-logo1.png',
          color: '#12477A',
          badgeClass: 'bg-[#12477A]'
        };
      case 'smart':
        return {
          title: 'Smart Search.',
          subtitle: 'Intelligent Parts Discovery Engine.',
          logo: '/images/smart-logo1.png',
          color: '#A64EC6',
          badgeClass: 'bg-[#A64EC6]'
        };
      default:
        return {
          title: source.toUpperCase(),
          subtitle: '',
          logo: '',
          color: '#666',
          badgeClass: 'bg-gray-500'
        };
    }
  };

  // Render source section with per-part fallback logic
  const renderSourceSection = (source: string) => {
    const partsForSource = partsGroupedBySource.get(source);
    if (!partsForSource || partsForSource.size === 0) return null;

    const sourceInfo = getSourceInfo(source);
    const partKeys: string[] = Array.from(partsForSource.keys()) as string[];
    let totalResults = 0;
    partsForSource.forEach((results: any[]) => {
      totalResults += results.length;
    });

    if (totalResults === 0) return null;

    return (
      <div key={source} className="mb-12">
        {/* Source Header */}
        <div className="flex items-center justify-between mb-5 px-4 sm:px-0 flex-col sm:flex-row gap-3 sm:gap-0" style={{ paddingBottom: '8px' }}>
          <div className="rs-cards-shelf-header flex items-center gap-3">
            <img
              src={sourceInfo.logo}
              alt={`${source} Logo`}
              className="h-8 w-auto object-contain"
              style={{ height: '32px' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div className="flex items-center gap-3">
              <div>
                <h2 className={`rs-cards-shelf-mainheader text-xl sm:text-[28px] block sm:inline ${source === 'smart' ? 'gradient-text' : ''}`}>
                  {sourceInfo.title}
                </h2>
                <span className="rs-cards-shelf-secondaryheader text-base sm:text-[28px] block sm:inline">
                  {' '}{sourceInfo.subtitle}
                </span>
              </div>
              <span className={`${sourceInfo.badgeClass} text-white text-sm font-semibold px-3 py-1 rounded-full`}>
                {totalResults}
              </span>
            </div>
          </div>
        </div>

        {/* Part Sections under this source */}
        {partKeys.map((partKey) => {
          const results = partsForSource.get(partKey) || [];
          if (results.length === 0) return null;

          const carouselKey = `${source}-${partKey}`;
          const scrollState = carouselScroll.get(carouselKey) || { canScrollLeft: false, canScrollRight: false };

          return (
            <div key={partKey} className="mb-8">
              {/* Part Subheading */}
              <div className="flex items-center gap-3 mb-4 px-4 sm:px-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
                  {partKey}
                </h3>
                <span className="bg-gray-200 text-gray-700 text-sm font-medium px-3 py-1 rounded-full">
                  {results.length} results
                </span>
              </div>

              {/* Carousel */}
              <div className="relative">
                {scrollState.canScrollLeft && (
                  <button
                    onClick={() => scrollCarousel(carouselKey, 'left')}
                    className="carousel-nav-button carousel-nav-left"
                    aria-label="Scroll left"
                  >
                    <CarouselArrowLeft />
                  </button>
                )}
                <div
                  ref={(el) => setCarouselRef(carouselKey, el)}
                  className="carousel-container -ml-4 sm:-ml-[60px] -mr-4 sm:-mr-[60px] pl-0 sm:pl-[60px] pr-0 sm:pr-[60px]"
                  onScroll={() => checkScrollPosition(carouselKey)}
                >
                  <div className="carousel-content">
                    {[...results].sort((a: any, b: any) => (b.brandPriority ? 1 : 0) - (a.brandPriority ? 1 : 0)).map((result: any) => renderResultCard(result, source))}
                  </div>
                </div>
                {scrollState.canScrollRight && (
                  <button
                    onClick={() => scrollCarousel(carouselKey, 'right')}
                    className="carousel-nav-button carousel-nav-right"
                    aria-label="Scroll right"
                  >
                    <CarouselArrowRight />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="App min-h-screen bg-[#F5F5F7] relative overflow-hidden">
      <main className="w-full py-6 sm:py-8 lg:py-10 relative z-10 px-4 sm:px-[60px]">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={handleBackToSearch}
            className="flex items-center gap-2 border-2 border-blue-600 hover:border-blue-700 text-blue-600 hover:text-blue-700 font-semibold px-4 py-2 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md group bg-white hover:bg-blue-50"
            aria-label="Back to search"
          >
            <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm sm:text-base font-semibold">Back to Search</span>
          </button>
        </div>

        {/* Query Display Section */}
        {displayTitle && (
          <div className="mb-8 mt-4">
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:flex-wrap gap-y-2 sm:gap-x-4">
              <span
                className="text-[#6e6e73] font-normal break-words"
                style={{
                  fontSize: '28px',
                  lineHeight: '1.1428571429',
                  letterSpacing: '0.007em',
                  fontFamily: 'SF Pro Display, SF Pro Icons, Helvetica Neue, Helvetica, Arial, sans-serif'
                }}
              >
                {displayTitle}
              </span>
            </div>
            {vehicleString && (
              <p className="text-sm text-gray-600 mt-2">{vehicleString}</p>
            )}
          </div>
        )}

        {/* Results Summary Banner */}
        {data && hasResults && (
          <div className={`${isLoading ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'} border rounded-2xl shadow-sm p-4 sm:p-5 mb-8`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                {isLoading ? (
                  <svg className="animate-spin w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                <p className={`${isLoading ? 'text-blue-800' : 'text-green-800'} font-semibold`}>
                  {isLoading
                    ? `Searching... Found ${getTotalDisplayedResults()} result(s) so far`
                    : `${data.totalParts} ${data.totalParts === 1 ? 'part' : 'parts'} searched | Found ${getTotalDisplayedResults()} result(s) from ${displaySources.map((s: string) => s === 'tvs' ? 'TVS' : s === 'boodmo' ? 'Boodmo' : 'Smart').join(', ')}`
                  }
                </p>
              </div>
              {!isLoading && data.responseTime > 0 && (
                <div className="text-green-700 font-medium">
                  {(data.responseTime / 1000).toFixed(2)}s
                </div>
              )}
            </div>
          </div>
        )}

        {/* Source Progress Indicators - Show while loading */}
        {/* Only show sources that are actively loading, completed, or errored - hide pending and skipped */}
        {sourceStatuses.filter(s => s.status === 'loading' || s.status === 'completed' || s.status === 'error').length > 0 && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-3 justify-center">
              {sourceStatuses.filter(s => s.status === 'loading' || s.status === 'completed' || s.status === 'error').map((status: SourceStatus) => {
                const sourceColor = status.source === 'tvs' ? '#FF6C16'
                  : status.source === 'boodmo' ? '#12477A' : '#A64EC6';
                const sourceName = status.source === 'tvs' ? 'TVS'
                  : status.source === 'boodmo' ? 'Boodmo' : 'Smart';

                // Get parts count info for display
                const partsSearchedCount = status.partsSearched?.length || 0;
                const partsFoundCount = status.partsFound?.length || 0;

                return (
                  <div
                    key={status.source}
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border"
                    style={{
                      borderColor: status.status === 'completed' ? sourceColor : '#e5e7eb',
                    }}
                  >
                    {status.status === 'loading' && (
                      <svg className="animate-spin w-4 h-4" style={{ color: sourceColor }} fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {status.status === 'completed' && (
                      <svg className="w-4 h-4" style={{ color: sourceColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {status.status === 'error' && (
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span
                      className="text-sm font-medium"
                      style={{
                        color: status.status === 'completed' ? sourceColor
                          : status.status === 'error' ? '#ef4444'
                          : '#6b7280'
                      }}
                    >
                      {sourceName}
                    </span>
                    {/* Show parts being searched while loading */}
                    {status.status === 'loading' && partsSearchedCount > 0 && (
                      <span className="text-xs text-gray-500">
                        ({partsSearchedCount} {partsSearchedCount === 1 ? 'part' : 'parts'})
                      </span>
                    )}
                    {/* Show results count when completed */}
                    {status.status === 'completed' && status.totalResults !== undefined && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: sourceColor }}
                      >
                        {status.totalResults}
                      </span>
                    )}
                    {/* Show parts found vs searched when completed */}
                    {status.status === 'completed' && partsSearchedCount > 0 && (
                      <span className="text-xs text-gray-500">
                        ({partsFoundCount}/{partsSearchedCount})
                      </span>
                    )}
                    {status.status === 'completed' && status.responseTime && status.responseTime > 0 && (
                      <span className="text-xs text-gray-400">
                        {(status.responseTime / 1000).toFixed(1)}s
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Loading spinner - only show if no source statuses yet */}
        {isLoading && sourceStatuses.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-4 text-gray-500">
            <svg className="animate-spin w-8 h-8" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Initializing search...</span>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-100 text-red-700 rounded-lg mb-6">
            <WarningIcon />
            <span>{error}</span>
          </div>
        )}

        {/* Results - Show progressively as sources complete */}
        {data && (
          <div>
            {/* Partial Failures Warning */}
            {data.partialFailures && data.partialFailures.length > 0 && (
              <div className="flex flex-wrap items-start gap-2 p-4 bg-yellow-100 text-yellow-800 rounded-lg mb-6 text-sm">
                <WarningIcon />
                <span>Some searches failed:</span>
                <ul className="w-full ml-6 list-disc">
                  {data.partialFailures.map((failure: { partKey: string; source?: string; error: string }, idx: number) => (
                    <li key={idx}>
                      {failure.partKey} ({failure.source}): {failure.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Main Results Header */}
            <div className="mb-6 mt-8 px-4 sm:px-0" style={{ paddingBottom: '8px' }}>
              <div className="rs-cards-shelf-header">
                <h2 className="rs-cards-shelf-mainheader text-3xl sm:text-[32px] font-bold">Search Results.</h2>
              </div>
            </div>

            {/* Show all sources with per-part fallback */}
            {displaySources.map(source => renderSourceSection(source))}

            {/* Empty State - Only show when done loading and no results */}
            {!isLoading && !hasResults && data.totalResults === 0 && (
              <div className="mt-16 px-4 sm:px-0 text-center">
                <div className="max-w-md mx-auto">
                  <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">No Results Found</h3>
                  <p className="text-gray-600 mb-6">
                    We couldn't find any parts matching your search. Try:
                  </p>
                  <ul className="text-left text-gray-600 space-y-2 mb-6">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Check your spelling or try different keywords</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Use more specific part names or part numbers</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Try searching for individual parts</span>
                    </li>
                  </ul>
                  <button
                    onClick={handleBackToSearch}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Start New Search
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default ResultsPage;
